import React, { useState, useEffect } from 'react';

interface AnimatedPodiumProps {
  animationDuration?: number; // Duration for each step in milliseconds
  colors?: {
    first?: string;
    second?: string;
    third?: string;
  };
  winners?: {
    first?: string;
    second?: string;
    third?: string;
  };
  scores?: {
    first?: number;
    second?: number;
    third?: number;
  };
  onPersonClick?: (personName: string) => void;
}

const AnimatedPodium: React.FC<AnimatedPodiumProps> = ({
  animationDuration = 500,
  colors = {
    first: '#FFD700',   // Gold
    second: '#C0C0C0',  // Silver
    third: '#CD7F32'    // Bronze
  },
  winners = {
    first: 'Platz 1',
    second: 'Platz 2', 
    third: 'Platz 3'
  },
  scores = {
    first: 0,
    second: 0,
    third: 0
  },
  onPersonClick
}) => {
  const [visiblePodiums, setVisiblePodiums] = useState<number[]>([]);
  const [visibleNames, setVisibleNames] = useState<number[]>([]);

  useEffect(() => {
    // Animation sequence: 3rd place -> 2nd place -> 1st place
    const animationSequence = [3, 2, 1];
    
    const timeouts: NodeJS.Timeout[] = [];
    
    animationSequence.forEach((position, index) => {
      // Podium animation
      const podiumTimeout = setTimeout(() => {
        setVisiblePodiums(prev => [...prev, position]);
      }, index * animationDuration);
      
      // Name animation (appears after podium is fully visible)
      const nameTimeout = setTimeout(() => {
        setVisibleNames(prev => [...prev, position]);
      }, (index * animationDuration) + animationDuration);
      
      timeouts.push(podiumTimeout, nameTimeout);
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [animationDuration]);

  const getPodiumStyle = (position: number) => {
    const heights = { 1: 90, 2: 60, 3: 40 };
    const isVisible = visiblePodiums.includes(position);
    
    return {
      width: '77px',
      height: isVisible ? `${heights[position as keyof typeof heights]}px` : '0px',
      backgroundColor: colors[position === 1 ? 'first' : position === 2 ? 'second' : 'third'],
      transition: `height ${animationDuration}ms ease-out`,
      borderRadius: '8px 8px 0 0',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative' as const,
    };
  };

  const getNumberStyle = (position: number) => {
    const isVisible = visiblePodiums.includes(position);
    
    return {
      color: 'white',
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '8px',
      opacity: isVisible ? 1 : 0,
      transition: `opacity ${animationDuration}ms ease-out ${animationDuration / 2}ms`,
      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    };
  };

  const getNameStyle = (position: number) => {
    const isVisible = visibleNames.includes(position);
    
    return {
      color: 'var(--ion-text-color)',
      fontSize: '18px', // Reduced font size
      fontWeight: 'bold',
      textAlign: 'center' as const,
      marginBottom: '6px', // Increased margin for better spacing
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: `opacity ${animationDuration / 2}ms ease-out, transform ${animationDuration / 2}ms ease-out`,
      maxWidth: '85px', // Further increased width
      width: '85px',
      wordWrap: 'break-word' as const,
      overflowWrap: 'break-word' as const,
      hyphens: 'auto' as const,
      lineHeight: '1.2', // Increased line height for better readability
      maxHeight: '50px', // Significantly increased max height for better containment
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2, // Keep at 2 lines
      WebkitBoxOrient: 'vertical' as const,
    };
  };

  const getScoreStyle = (position: number) => {
    const isVisible = visibleNames.includes(position);
    
    return {
      color: '#ffd700',
      fontSize: '16px', // Reduced font size
      fontWeight: '600',
      textAlign: 'center' as const,
      marginBottom: '6px', // Increased margin for better spacing
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: `opacity ${animationDuration / 2}ms ease-out ${animationDuration / 4}ms, transform ${animationDuration / 2}ms ease-out ${animationDuration / 4}ms`,
    };
  };

  const truncateName = (name: string, maxWidth: number = 90) => { // Increased maxWidth further
    if (!name) return '';
    
    // Erstelle ein temporäres Element zum Messen der Textbreite
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return name;
    
    context.font = 'bold 18px sans-serif'; // Updated to match new font size
    
    // Prüfe ob der Name bereits in die Breite passt
    if (context.measureText(name).width <= maxWidth) {
      return name;
    }
    
    // Versuche Umbruch an Leerzeichen
    const words = name.split(' ');
    if (words.length > 1) {
      // Prüfe ob alle Wörter einzeln in eine Zeile passen
      const longestWord = words.reduce((a, b) => a.length > b.length ? a : b);
      if (context.measureText(longestWord).width <= maxWidth) {
        return name; // Lass CSS den Umbruch machen
      }
    }
    
    // Kürze den Namen mit "..."
    let truncated = name;
    const ellipsis = '...';
    const ellipsisWidth = context.measureText(ellipsis).width;
    
    while (context.measureText(truncated).width + ellipsisWidth > maxWidth && truncated.length > 1) {
      truncated = truncated.slice(0, -1);
    }
    
    return truncated + ellipsis;
  };

  const getWinnerName = (position: number) => {
    let name = '';
    switch (position) {
      case 1:
        name = winners.first || '';
        break;
      case 2:
        name = winners.second || '';
        break;
      case 3:
        name = winners.third || '';
        break;
      default:
        return '';
    }
    
    return truncateName(name);
  };

  const getWinnerScore = (position: number) => {
    switch (position) {
      case 1:
        return scores.first;
      case 2:
        return scores.second;
      case 3:
        return scores.third;
      default:
        return 0;
    }
  };

  const handlePersonClick = (position: number) => {
    if (!onPersonClick) return;
    
    const name = getWinnerName(position);
    if (name && name !== 'Platz 1' && name !== 'Platz 2' && name !== 'Platz 3') {
      onPersonClick(name);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      gap: '15px', // Further reduced gap
      width: '100%',
      height: '190px', // Significantly increased height
      padding: '15px 0',
      overflow: 'hidden', // Prevent content from overflowing
    }}>
      {/* 2nd Place (Left) */}
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          cursor: onPersonClick ? 'pointer' : 'default'
        }}
        onClick={() => handlePersonClick(2)}
      >
        <div style={getNameStyle(2)}>
          {getWinnerName(2)}
        </div>
        <div style={getScoreStyle(2)}>
          {getWinnerScore(2)}
        </div>
        <div style={getPodiumStyle(2)}>
          <span style={getNumberStyle(2)}>2</span>
        </div>
      </div>
      
      {/* 1st Place (Center) */}
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          cursor: onPersonClick ? 'pointer' : 'default'
        }}
        onClick={() => handlePersonClick(1)}
      >
        <div style={getNameStyle(1)}>
          {getWinnerName(1)}
        </div>
        <div style={getScoreStyle(1)}>
          {getWinnerScore(1)}
        </div>
        <div style={getPodiumStyle(1)}>
          <span style={getNumberStyle(1)}>1</span>
        </div>
      </div>
      
      {/* 3rd Place (Right) */}
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          cursor: onPersonClick ? 'pointer' : 'default'
        }}
        onClick={() => handlePersonClick(3)}
      >
        <div style={getNameStyle(3)}>
          {getWinnerName(3)}
        </div>
        <div style={getScoreStyle(3)}>
          {getWinnerScore(3)}
        </div>
        <div style={getPodiumStyle(3)}>
          <span style={getNumberStyle(3)}>3</span>
        </div>
      </div>
    </div>
  );
};

export default AnimatedPodium;
